/**
 * jsonInspectorDialogStore — global open/close state for the JSON details
 * inspector dialog launched from outside the MetaInfo manager (grid header
 * "JSON details" menu, cell context menu on a `declared-json` column). The
 * dialog mounts once at the app level via `GlobalJsonInspectorDialog` so any
 * feature can pop it by calling `openJsonInspectorDialog(…)` without
 * prop-drilling.
 */

import { create } from 'zustand';
import type { DatabaseEngineId } from '../../../../../core/engines/engineTypes';

export type JsonInspectorDialogContext = {
  connectionId: string;
  database: string;
  schema: string;
  table: string;
  column: string;
  engine: DatabaseEngineId;
  /** Engine-typed column type (e.g. `jsonb`, `nvarchar(max)`). Drives the
   * "Native JSON column" informational note inside the inspector dialog. */
  dataType?: string;
  /** Source label for analytics (`'header-menu'`, `'cell-menu'`, …). */
  source: string;
};

type JsonInspectorDialogState = {
  isOpen: boolean;
  context: JsonInspectorDialogContext | null;
};

type JsonInspectorDialogActions = {
  openDialog: (ctx: JsonInspectorDialogContext) => void;
  closeDialog: () => void;
};

type JsonInspectorDialogStore = JsonInspectorDialogState &
  JsonInspectorDialogActions;

export const useJsonInspectorDialogStore = create<JsonInspectorDialogStore>(
  (set) => ({
    isOpen: false,
    context: null,
    openDialog: (ctx) => {
      // Defer one frame so an in-flight Radix ContextMenu (or other overlay)
      // close finishes BEFORE this Dialog mounts. Mounting a Radix Dialog
      // mid-close on the menu leaves `body { pointer-events: none }` stuck
      // because two overlay layers race their pointer-events locks.
      const open = () => set({ isOpen: true, context: ctx });
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(open);
      } else {
        setTimeout(open, 0);
      }
    },
    closeDialog: () => set({ isOpen: false }),
  }),
);

export const openJsonInspectorDialog = (ctx: JsonInspectorDialogContext) =>
  useJsonInspectorDialogStore.getState().openDialog(ctx);
